import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import hrService from '../../../services/hrService';
import PayrollManagement from './PayrollManagement';
import { formatEasternDateOnly } from '../../../utils/timezone';

const HROperations = () => {
  const [selectedTab, setSelectedTab] = useState('leave_requests');
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [processingLeaves, setProcessingLeaves] = useState(new Set());
  const [payrollItems, setPayrollItems] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // FIXED: Enhanced URL parameter handling for proper tab detection
  useEffect(() => {
    const urlParams = new URLSearchParams(location?.search);
    const tabParam = urlParams?.get('tab');
    
    console.log('URL tab parameter:', tabParam);
    console.log('Current location:', location?.pathname, location?.search);
    
    // Enhanced tab parameter mapping with robust detection
    if (tabParam === 'payroll') {
      console.log('Setting payroll tab from URL parameter');
      setSelectedTab('payroll');
    } else if (tabParam === 'leave-management') {
      console.log('Setting leave_requests tab from URL parameter');
      setSelectedTab('leave_requests');
    } else if (tabParam === 'leave_balances') {
      console.log('Setting leave_balances tab from URL parameter');  
      setSelectedTab('leave_balances');
    } else {
      // FIXED: Better context detection for payroll navigation
      const currentPath = location?.pathname;
      const currentSearch = location?.search;
      
      // Check if we're in a payroll context by examining the full URL
      if (currentPath?.includes('admin-dashboard') && currentSearch?.includes('payroll')) {
        console.log('Detected payroll context from URL search');
        setSelectedTab('payroll');
      } else if (currentPath?.includes('admin-dashboard') && !tabParam) {
        // Default tab for admin dashboard without specific tab parameter
        setSelectedTab('leave_requests');
      } else {
        setSelectedTab('leave_requests');
      }
    }
  }, [location?.search, location?.pathname]);

  useEffect(() => {
    loadData();
  }, [selectedTab]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Loading data for tab:', selectedTab);
      
      if (selectedTab === 'leave_requests') {
        const leaves = await hrService?.getLeaveRequests({ status: 'pending' });
        setPendingLeaves(leaves || []);
        console.log('Loaded leave requests:', leaves?.length || 0);
      } else if (selectedTab === 'payroll') {
        // FIXED: Enhanced payroll data loading with better error handling
        console.log('Loading payroll data...');
        try {
          const pendingPayroll = await hrService?.getPendingPayrollItems();
          setPayrollItems(pendingPayroll || []);
          console.log('Successfully loaded payroll items:', pendingPayroll?.length || 0);
        } catch (payrollError) {
          console.error('Error loading payroll data:', payrollError);
          setError(`Failed to load payroll data: ${payrollError?.message}`);
          setPayrollItems([]);
        }
      } else if (selectedTab === 'leave_balances') {
        console.log('Loading leave balances...');
        try {
          const balanceResult = await hrService?.getLeaveBalances();
          
          // ENHANCED: Handle structured error response from enhanced getLeaveBalances
          if (balanceResult?.error === 'NO_EMPLOYEE_RECORD') {
            console.warn('No employee record found for leave balances:', balanceResult?.message);
            setError(balanceResult?.message);
            setLeaveBalances([]);
          } else if (Array.isArray(balanceResult)) {
            // Normal array response
            setLeaveBalances(balanceResult || []);
            console.log('Successfully loaded leave balances:', balanceResult?.length || 0);
          } else if (balanceResult?.balances) {
            // Structured response with balances array
            setLeaveBalances(balanceResult?.balances || []);
            console.log('Successfully loaded leave balances:', balanceResult?.balances?.length || 0);
          } else {
            // Fallback
            setLeaveBalances([]);
            console.log('No leave balances found');
          }
        } catch (balanceError) {
          console.error('Error loading leave balances:', balanceError);
          
          // ENHANCED: Better error handling for different error types
          if (balanceError?.message?.includes('Employee record not found')) {
            setError('Employee record not found. Please contact HR to set up your employee profile.');
          } else {
            setError(`Failed to load leave balances: ${balanceError?.message}`);
          }
          setLeaveBalances([]);
        }
      }
    } catch (err) {
      console.error('Error loading HR operations data:', err);
      setError(err?.message || 'Failed to load data');
      
      // Reset arrays on error
      setPendingLeaves([]);
      setPayrollItems([]);
      setLeaveBalances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveAction = async (leaveId, action) => {
    if (processingLeaves?.has(leaveId)) return;
    
    setProcessingLeaves(prev => new Set(prev)?.add(leaveId));
    
    try {
      if (action === 'approve') {
        await hrService?.approveLeaveRequest(leaveId, true);
      } else if (action === 'reject') {
        await hrService?.approveLeaveRequest(leaveId, false);
      }
      
      // Remove from pending list immediately for better UX
      setPendingLeaves(prev => prev?.filter(leave => leave?.id !== leaveId));
    } catch (err) {
      console.error(`Error ${action}ing leave request:`, err);
      setError(`Failed to ${action} leave request`);
    } finally {
      setProcessingLeaves(prev => {
        const newSet = new Set(prev);
        newSet?.delete(leaveId);
        return newSet;
      });
    }
  };

  const handlePayrollAction = (item) => {
    // Navigate to employee profile for more details
    navigate(`/employee-profile?employeeId=${item?.employeeId}`);
  };

  const handleProcessPayroll = async (item) => {
    if (processing) return;
    
    setProcessing(true);
    try {
      const payrollData = {
        employeeId: item?.employeeId,
        baseSalary: item?.baseSalary || item?.amount,
        payPeriodStart: item?.payPeriodStart,
        payPeriodEnd: item?.payPeriodEnd,
        bonuses: 0,
        deductions: 0,
        overtimeHours: 0,
        hourlyRate: item?.hourlyRate || 0
      };

      await hrService?.createPayrollRecord(payrollData);
      
      // Remove from pending list
      setPayrollItems(prev => prev?.filter(p => p?.id !== item?.id));
    } catch (err) {
      console.error('Error processing payroll:', err);
      setError(`Failed to process payroll: ${err?.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // FIXED: Enhanced leave balance processing with individual employee handling
  const handleProcessLeaveBalance = async (employeeId) => {
    if (processing) return;
    
    setProcessing(true);
    try {
      const currentDate = new Date();
      const currentMonth = currentDate?.getMonth() + 1;
      const currentYear = currentDate?.getFullYear();

      await hrService?.processLeaveBalanceForEmployee(employeeId, currentYear, currentMonth);
      
      // Reload leave balances after processing
      if (selectedTab === 'leave_balances') {
        await loadData();
      }
    } catch (err) {
      console.error('Error processing leave balance:', err);
      setError(`Failed to process leave balance: ${err?.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // FIXED: Bulk leave balance processing for all employees with improved duplicate handling
  const handleProcessAllLeaveBalances = async () => {
    if (processing) return;
    
    setProcessing(true);
    setError(''); // Clear any previous errors
    
    try {
      const currentDate = new Date();
      const currentMonth = currentDate?.getMonth() + 1;
      const currentYear = currentDate?.getFullYear();

      // Process leave balance for all employees
      const result = await hrService?.processMonthlyLeaveBalance(currentYear, currentMonth);
      
      if (result?.processedCount > 0) {
        console.log(`Processed leave balance for ${result?.processedCount} employees`);
        // Reload leave balances after processing
        if (selectedTab === 'leave_balances') {
          await loadData();
        }
      }
    } catch (err) {
      console.error('Error processing all leave balances:', err);
      
      // CRITICAL FIX: Handle duplicate prevention errors gracefully
      if (err?.message?.includes('DUPLICATE_PREVENTION') || 
          err?.message?.includes('already exists')) {
        // Show user-friendly message for duplicate records
        setError('Leave balances for this month have already been processed. The existing records are shown below.');
        
        // Reload leave balances to show the existing records
        if (selectedTab === 'leave_balances') {
          await loadData();
        }
      } else {
        // Show generic error for other issues
        setError(`Failed to process leave balances: ${err?.message}`);
      }
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatEasternDateOnly(dateString, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const renderEmptyState = (type) => {
    const content = type === 'leave_requests' 
      ? {
          icon: 'Calendar',
          title: 'No pending leave requests',
          description: 'All leave requests have been processed or no new requests available.'
        }
      : type === 'payroll'
      ? {
          icon: 'DollarSign',
          title: 'No pending payroll items',
          description: 'All payroll processing is up to date.'
        }
      : {
          icon: 'BarChart3',
          title: 'No leave balance records',
          description: 'Leave balances will appear here once processed.'
        };

    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name={content?.icon} size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">{content?.title}</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">{content?.description}</p>
      </div>
    );
  };

  const renderLoadingState = () => (
    <div className="space-y-4">
      {[1, 2, 3]?.map((i) => (
        <div
          key={i}
          className="flex items-center justify-between p-4 rounded-lg border border-border animate-pulse"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-full bg-muted"></div>
            <div className="space-y-2">
              <div className="w-32 h-4 bg-muted rounded"></div>
              <div className="w-48 h-3 bg-muted rounded"></div>
              <div className="w-24 h-3 bg-muted rounded"></div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-20 h-8 bg-muted rounded"></div>
            <div className="w-20 h-8 bg-muted rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderErrorState = () => (
    <div className="text-center py-12">
      <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
      </div>
      <h3 className="text-lg font-medium text-destructive mb-2">Error loading data</h3>
      <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">{error}</p>
      <Button onClick={loadData} variant="outline" size="sm">
        <Icon name="RefreshCw" size={16} className="mr-2" />
        Try Again
      </Button>
    </div>
  );

  // FIXED: Enhanced tab switching with better state management and URL updates
  const handleTabSwitch = (newTab) => {
    console.log('Switching to tab:', newTab);
    setSelectedTab(newTab);
    setError(''); // Clear any previous errors
    
    // FIXED: Proper URL updating to reflect tab changes
    const currentPath = window.location?.pathname;
    let newUrl;
    
    if (newTab === 'payroll') {
      newUrl = `${currentPath}?tab=payroll`;
    } else if (newTab === 'leave_balances') {
      newUrl = `${currentPath}?tab=leave_balances`;
    } else {
      newUrl = `${currentPath}?tab=leave-management`;
    }
    
    // Update URL without causing page reload
    window.history?.pushState(null, '', newUrl);
  };

  if (error && !loading) {
    return (
      <div className="bg-card rounded-lg border border-border card-shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">HR Operations</h2>
        </div>
        {renderErrorState()}
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">HR Operations</h2>
        <div className="flex items-center space-x-2">
          <Button
            variant={selectedTab === 'leave_requests' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleTabSwitch('leave_requests')}
          >
            <Icon name="Calendar" size={16} className="mr-2" />
            Leave Requests
          </Button>
          <Button
            variant={selectedTab === 'payroll' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleTabSwitch('payroll')}
          >
            <Icon name="DollarSign" size={16} className="mr-2" />
            Payroll
          </Button>
          <Button
            variant={selectedTab === 'leave_balances' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleTabSwitch('leave_balances')}
          >
            <Icon name="BarChart3" size={16} className="mr-2" />
            Leave Balances
          </Button>
        </div>
      </div>

      {/* Enhanced loading and error states with tab context */}
      {loading ? (
        <div className="space-y-4">
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground">
              Loading {selectedTab === 'payroll' ? 'payroll data' : 
                      selectedTab === 'leave_requests'? 'leave requests' : 'leave balances'}...
            </p>
          </div>
          {renderLoadingState()}
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
          </div>
          <h3 className="text-lg font-medium text-destructive mb-2">Error loading {selectedTab} data</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">{error}</p>
          <Button onClick={loadData} variant="outline" size="sm">
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Retry Loading
          </Button>
        </div>
      ) : (
        <>
          {/* Tab Content Rendering */}
          {selectedTab === 'leave_requests' && (
            <>
              {pendingLeaves?.length === 0 ? (
                renderEmptyState('leave_requests')
              ) : (
                <div className="space-y-4">
                  {pendingLeaves?.map((leave) => {
                    const isProcessing = processingLeaves?.has(leave?.id);
                    return (
                      <div
                        key={leave?.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-background/50 transition-colors"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <Icon name="Calendar" size={20} color="var(--color-primary)" />
                          </div>
                          
                          <div className="space-y-1">
                            <p className="font-medium text-foreground">
                              {leave?.employees?.userProfiles?.fullName || leave?.employees?.full_name || 'Unknown Employee'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              <span className="capitalize">{leave?.leaveType?.replace('_', ' ') || 'Leave'}</span> • 
                              {' '}{formatDate(leave?.startDate)} - {formatDate(leave?.endDate)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {leave?.totalDays || 0} day{(leave?.totalDays || 0) !== 1 ? 's' : ''} 
                              {leave?.reason && ` • ${leave?.reason}`}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLeaveAction(leave?.id, 'reject')}
                            disabled={isProcessing}
                            className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50"
                          >
                            <Icon name="X" size={14} className="mr-1" />
                            {isProcessing ? 'Processing...' : 'Reject'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLeaveAction(leave?.id, 'approve')}
                            disabled={isProcessing}
                            className="text-green-600 border-green-200 hover:bg-green-50 disabled:opacity-50"
                          >
                            <Icon name="Check" size={14} className="mr-1" />
                            {isProcessing ? 'Processing...' : 'Approve'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* FIXED: Replace payroll tab content with comprehensive PayrollManagement component */}
          {selectedTab === 'payroll' && (
            <PayrollManagement />
          )}

          {/* FIXED: Enhanced leave balances tab with bulk processing options */}
          {selectedTab === 'leave_balances' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground">Leave Balance Management</h3>
                  <p className="text-sm text-muted-foreground">
                    Process and manage employee leave balances
                  </p>
                </div>
                <Button
                  onClick={handleProcessAllLeaveBalances}
                  disabled={processing}
                  variant="default"
                  size="sm"
                >
                  <Icon name="RefreshCw" size={16} className="mr-2" />
                  {processing ? 'Processing...' : 'Process All Balances'}
                </Button>
              </div>

              {leaveBalances?.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="BarChart3" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">No leave balance records</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto mb-6">
                    Leave balances will appear here once processed. Click "Process All Balances" to generate monthly leave balance records.
                  </p>
                  <Button onClick={handleProcessAllLeaveBalances} disabled={processing} variant="default" size="sm">
                    <Icon name="RefreshCw" size={16} className="mr-2" />
                    {processing ? 'Processing...' : 'Process All Balances'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {leaveBalances?.map((balance) => (
                    <div
                      key={balance?.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-background/50 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <Icon name="BarChart3" size={20} color="rgb(59, 130, 246)" />
                        </div>
                        
                        <div className="space-y-1">
                          <p className="font-medium text-foreground">
                            {balance?.employees?.userProfiles?.fullName || 'Unknown Employee'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {balance?.year}/{String(balance?.month)?.padStart(2, '0')} • 
                            Opening: {balance?.openingBalance || 0} days
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Earned: {balance?.earnedBalance || 0} • Used: {balance?.usedBalance || 0} • 
                            Closing: {balance?.closingBalance || 0}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          balance?.isEligibleForAccrual 
                            ? 'bg-green-100 text-green-700' :'bg-gray-100 text-gray-700'
                        }`}>
                          {balance?.isEligibleForAccrual ? 'Eligible' : 'Not Eligible'}
                        </span>
                        <div className="text-right">
                          <div className="text-sm font-medium text-foreground">
                            {balance?.closingBalance || 0} days
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Available
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleProcessLeaveBalance(balance?.employees?.id)}
                          disabled={processing}
                        >
                          <Icon name="RefreshCw" size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default HROperations;